import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Crown } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { API } from "../lib/site";
import { PLAN_INFO, HOTEL_TYPES, formatPrice } from "../lib/plans";

export const HOTEL_TOKEN_KEY = "ggh_hotel_token";

const initialForm = {
    hotel_name: "",
    location: "",
    contact_name: "",
    email: "",
    password: "",
    phone: "",
    rooms: "",
    hotel_type: "",
};

const Join = () => {
    const [params] = useSearchParams();
    const [plan, setPlan] = useState(params.get("plan") || "professional");
    const [prices, setPrices] = useState({});
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API}/plans`).then((res) => setPrices(res.data.prices || {})).catch(() => {});
    }, []);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (form.hotel_name.trim().length < 2) er.hotel_name = "Please enter the hotel name.";
        if (form.location.trim().length < 2) er.location = "Please enter the hotel location.";
        if (form.contact_name.trim().length < 2) er.contact_name = "Please enter the contact person's name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid business email.";
        if (form.password.length < 8) er.password = "Password must be at least 8 characters.";
        const rooms = parseInt(form.rooms, 10);
        if (!rooms || rooms < 1) er.rooms = "Please enter the number of rooms.";
        if (!form.hotel_type) er.hotel_type = "Please choose the hotel type.";
        return er;
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("submitting");
        try {
            const { data } = await axios.post(`${API}/hotels/register`, {
                ...form,
                rooms: parseInt(form.rooms, 10),
                plan,
            });
            localStorage.setItem(HOTEL_TOKEN_KEY, data.token);
            toast.success("Welcome to GGH — your membership is active.");
            navigate("/hotel/dashboard?welcome=1");
        } catch (err) {
            setStatus("idle");
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not create your account. Please try again.");
        }
    };

    const labelCls = "mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#1A3B2B]/70";
    const errCls = "mt-2 text-xs font-semibold text-red-800";

    return (
        <main>
            <SEO
                title="Join GGH | Global Green Hotel Membership"
                description="Choose a GGH membership plan, create your hotel account and start measuring, improving and demonstrating your sustainability impact."
            />
            <PageHero
                testid="join-hero"
                eyebrow="Join GGH"
                titleLines={["Create your", "hotel account"]}
                description="Choose your plan and enter your hotel information — your account and dashboard are ready instantly."
            />

            <section data-testid="join-section" className="py-24 sm:py-32">
                <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>Step 1 — Choose Your Plan</Eyebrow>
                        <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="join-plans">
                            {PLAN_INFO.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    data-testid={`join-plan-${p.key}`}
                                    onClick={() => setPlan(p.key)}
                                    aria-pressed={plan === p.key}
                                    className={`relative rounded-2xl border p-6 text-left transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 ${
                                        plan === p.key
                                            ? "border-[#1A3B2B] bg-white shadow-[0_18px_44px_rgba(10,23,17,0.10)]"
                                            : "border-[#E8E6E1] bg-white/60 hover:border-[#C2A160]"
                                    }`}
                                >
                                    {p.popular && (
                                        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[#C2A160] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                            <Crown size={11} aria-hidden="true" /> Most Popular
                                        </span>
                                    )}
                                    <span className="font-display block text-xl font-medium text-[#0A1711]">{p.name}</span>
                                    <span className="mt-2 block text-xs leading-relaxed text-[#4A5D54]">{p.tagline}</span>
                                </button>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <form onSubmit={submit} noValidate data-testid="join-form" className="mt-14">
                            <Eyebrow>Step 2 — Hotel Information & Account</Eyebrow>
                            <div className="mt-8 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="jn-name" className={labelCls}>Hotel Name *</label>
                                    <input id="jn-name" data-testid="join-hotel-name" type="text" value={form.hotel_name} onChange={set("hotel_name")} aria-invalid={!!errors.hotel_name} className={`input-field ${errors.hotel_name ? "input-error" : ""}`} placeholder="e.g. Oceanview Resort" />
                                    {errors.hotel_name && <p className={errCls} role="alert">{errors.hotel_name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-location" className={labelCls}>Hotel Location *</label>
                                    <input id="jn-location" data-testid="join-location" type="text" value={form.location} onChange={set("location")} aria-invalid={!!errors.location} className={`input-field ${errors.location ? "input-error" : ""}`} placeholder="City, State/Region, Country" />
                                    {errors.location && <p className={errCls} role="alert">{errors.location}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-contact" className={labelCls}>Contact Person *</label>
                                    <input id="jn-contact" data-testid="join-contact-name" type="text" value={form.contact_name} onChange={set("contact_name")} aria-invalid={!!errors.contact_name} className={`input-field ${errors.contact_name ? "input-error" : ""}`} placeholder="Full name" />
                                    {errors.contact_name && <p className={errCls} role="alert">{errors.contact_name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-email" className={labelCls}>Business Email *</label>
                                    <input id="jn-email" data-testid="join-email" type="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} className={`input-field ${errors.email ? "input-error" : ""}`} placeholder="you@yourhotel.com" autoComplete="email" />
                                    {errors.email && <p className={errCls} role="alert">{errors.email}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-password" className={labelCls}>Create Password *</label>
                                    <input id="jn-password" data-testid="join-password" type="password" value={form.password} onChange={set("password")} aria-invalid={!!errors.password} className={`input-field ${errors.password ? "input-error" : ""}`} placeholder="At least 8 characters" autoComplete="new-password" />
                                    {errors.password && <p className={errCls} role="alert">{errors.password}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-phone" className={labelCls}>
                                        Phone <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span>
                                    </label>
                                    <input id="jn-phone" data-testid="join-phone" type="tel" value={form.phone} onChange={set("phone")} className="input-field" placeholder="+1 …" />
                                </div>
                                <div>
                                    <label htmlFor="jn-rooms" className={labelCls}>Number of Rooms *</label>
                                    <input id="jn-rooms" data-testid="join-rooms" type="number" min="1" value={form.rooms} onChange={set("rooms")} aria-invalid={!!errors.rooms} className={`input-field ${errors.rooms ? "input-error" : ""}`} placeholder="e.g. 120" />
                                    {errors.rooms && <p className={errCls} role="alert">{errors.rooms}</p>}
                                </div>
                                <div>
                                    <label htmlFor="jn-type" className={labelCls}>Hotel Type *</label>
                                    <select id="jn-type" data-testid="join-hotel-type" value={form.hotel_type} onChange={set("hotel_type")} aria-invalid={!!errors.hotel_type} className={`input-field ${errors.hotel_type ? "input-error" : ""}`}>
                                        <option value="">Select type…</option>
                                        {HOTEL_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                    {errors.hotel_type && <p className={errCls} role="alert">{errors.hotel_type}</p>}
                                </div>
                            </div>

                            <div className="mt-10 flex flex-wrap items-center gap-5">
                                <button
                                    type="submit"
                                    data-testid="join-submit"
                                    disabled={status === "submitting"}
                                    className="btn-green disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {status === "submitting" ? "Creating your account…" : "Create Account & Open Dashboard"}
                                </button>
                                <p className="max-w-md text-xs leading-relaxed text-[#4A5D54]">
                                    Step 3 — your account is created instantly. No payment is
                                    required at this stage.
                                </p>
                            </div>
                        </form>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <p className="mt-10 flex items-center gap-2 text-sm text-[#4A5D54]">
                            <CheckCircle2 size={16} className="text-[#1A3B2B]" aria-hidden="true" />
                            Already have a hotel account?{" "}
                            <Link to="/hotel/login" data-testid="join-login-link" className="font-bold text-[#1A3B2B] underline-offset-4 hover:underline">
                                Hotel Login
                            </Link>
                        </p>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default Join;
